/**
 * Mock Tenant Data
 * Sample tenants for development and testing
 */

import type {
  Tenant,
  TenantType,
  Subscription,
  PlanCode,
} from '@/types/tenant.types'

// ============================================================================
// MOCK TENANTS
// ============================================================================

/**
 * Solo CA Tenant - Individual practitioner
 */
export const soloTenant: Tenant = {
  tenantId: 'tenant-001-solo',
  tenantCode: 'TH-Tenant-001',
  tenantName: 'Rajesh Kumar & Associates',
  tenantType: 'solo_ca',
  status: 'active',

  dbHost: 'localhost',
  dbPort: 5432,
  dbName: 'taxhive_tenant_001',
  dbUser: 'tenant_001_user',

  objectBucket: 'taxhive-obj-001',

  firmGstin: '27AABCU9603R1ZM',
  firmPan: 'AABCU9603R',
  firmEmail: 'rajesh@rkassociates.in',
  firmPhone: '9876543210',
  firmWebsite: 'https://rkassociates.in',
  firmAddress: {
    line1: '101, Shivaji Nagar',
    line2: 'Near Railway Station',
    city: 'Pune',
    state: 'Maharashtra',
    pincode: '411005',
    country: 'India',
  },
  logoUrl: undefined,

  settings: {
    dateFormat: 'DD/MM/YYYY',
    financialYearStart: '04-01',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    notificationPreferences: {
      email: true,
      sms: true,
    },
  },
  metadata: {},

  createdAt: '2024-01-15T10:30:00Z',
  updatedAt: '2024-12-20T14:45:00Z',
}

/**
 * Medium Firm Tenant - Small CA firm
 */
export const mediumTenant: Tenant = {
  tenantId: 'tenant-002-medium',
  tenantCode: 'TH-Tenant-002',
  tenantName: 'Sharma & Patel Chartered Accountants',
  tenantType: 'medium_firm',
  status: 'active',

  dbHost: 'localhost',
  dbPort: 5432,
  dbName: 'taxhive_tenant_002',
  dbUser: 'tenant_002_user',

  objectBucket: 'taxhive-obj-002',

  firmGstin: '29AADCS1234M1Z5',
  firmPan: 'AADCS1234M',
  firmEmail: 'info@sharmapatel.co.in',
  firmPhone: '9988776655',
  firmWebsite: 'https://sharmapatel.co.in',
  firmAddress: {
    line1: '5th Floor, Prestige Tower',
    line2: 'MG Road',
    city: 'Bangalore',
    state: 'Karnataka',
    pincode: '560001',
    country: 'India',
  },
  logoUrl: undefined,

  settings: {
    dateFormat: 'DD/MM/YYYY',
    financialYearStart: '04-01',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    notificationPreferences: {
      email: true,
      sms: false,
    },
  },
  metadata: {},

  createdAt: '2023-06-01T09:00:00Z',
  updatedAt: '2024-12-18T16:30:00Z',
}

/**
 * Large Firm Tenant - Enterprise CA firm
 */
export const largeTenant: Tenant = {
  tenantId: 'tenant-003-large',
  tenantCode: 'TH-Tenant-003',
  tenantName: 'Mehta Associates LLP',
  tenantType: 'large_firm',
  status: 'active',

  dbHost: 'localhost',
  dbPort: 5432,
  dbName: 'taxhive_tenant_003',
  dbUser: 'tenant_003_user',

  objectBucket: 'taxhive-obj-003',

  firmGstin: '22AADFM5678P1ZK',
  firmPan: 'AADFM5678P',
  firmEmail: 'contact@mehtallp.com',
  firmPhone: '02226543210',
  firmWebsite: 'https://mehtallp.com',
  firmAddress: {
    line1: 'Nariman Point',
    line2: 'Express Towers, 12th Floor',
    city: 'Mumbai',
    state: 'Maharashtra',
    pincode: '400021',
    country: 'India',
  },
  logoUrl: undefined,

  settings: {
    dateFormat: 'DD/MM/YYYY',
    financialYearStart: '04-01',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    notificationPreferences: {
      email: true,
      sms: true,
    },
  },
  metadata: {
    branches: 5,
    established: 1985,
  },

  createdAt: '2022-01-10T08:00:00Z',
  updatedAt: '2024-12-19T11:15:00Z',
}

/**
 * All mock tenants
 */
export const mockTenants: Tenant[] = [soloTenant, mediumTenant, largeTenant]

// ============================================================================
// MOCK SUBSCRIPTIONS
// ============================================================================

export const soloSubscription: Subscription = {
  subscriptionId: 'sub-001',
  tenantId: 'tenant-001-solo',
  planCode: 'starter',
  status: 'active',
  billingCycle: 'yearly',
  startsAt: '2024-01-15T00:00:00Z',
  expiresAt: '2025-01-15T00:00:00Z',
  maxUsers: 3,
  maxClients: 50,
  paymentMethod: {
    type: 'card',
    last4: '4242',
    brand: 'Visa',
  },
  lastPaymentAt: '2024-01-15T00:00:00Z',
  createdAt: '2024-01-15T00:00:00Z',
  updatedAt: '2024-01-15T00:00:00Z',
}

export const mediumSubscription: Subscription = {
  subscriptionId: 'sub-002',
  tenantId: 'tenant-002-medium',
  planCode: 'professional',
  status: 'active',
  billingCycle: 'yearly',
  startsAt: '2023-06-01T00:00:00Z',
  expiresAt: '2025-06-01T00:00:00Z',
  maxUsers: 15,
  maxClients: 500,
  maxBranches: 3,
  paymentMethod: {
    type: 'netbanking',
  },
  lastPaymentAt: '2024-06-01T00:00:00Z',
  createdAt: '2023-06-01T00:00:00Z',
  updatedAt: '2024-06-01T00:00:00Z',
}

export const largeSubscription: Subscription = {
  subscriptionId: 'sub-003',
  tenantId: 'tenant-003-large',
  planCode: 'enterprise',
  status: 'active',
  billingCycle: 'yearly',
  startsAt: '2022-01-10T00:00:00Z',
  expiresAt: '2025-01-10T00:00:00Z',
  maxUsers: 9999,
  maxClients: 99999,
  maxBranches: 99,
  paymentMethod: {
    type: 'card',
    last4: '1234',
    brand: 'Mastercard',
  },
  lastPaymentAt: '2024-01-10T00:00:00Z',
  createdAt: '2022-01-10T00:00:00Z',
  updatedAt: '2024-01-10T00:00:00Z',
}

export const mockSubscriptions: Subscription[] = [
  soloSubscription,
  mediumSubscription,
  largeSubscription,
]

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get tenant by ID
 */
export function getTenantById(tenantId: string): Tenant | undefined {
  return mockTenants.find((t) => t.tenantId === tenantId)
}

/**
 * Get tenant by code
 */
export function getTenantByCode(tenantCode: string): Tenant | undefined {
  return mockTenants.find((t) => t.tenantCode === tenantCode)
}

/**
 * Get subscription for tenant
 */
export function getSubscriptionForTenant(tenantId: string): Subscription | undefined {
  return mockSubscriptions.find((s) => s.tenantId === tenantId)
}

/**
 * Get tenants by type
 */
export function getTenantsByType(type: TenantType): Tenant[] {
  return mockTenants.filter((t) => t.tenantType === type)
}

/**
 * Get tenants by plan
 */
export function getTenantsByPlan(planCode: PlanCode): Tenant[] {
  const subscriptions = mockSubscriptions.filter((s) => s.planCode === planCode)
  const tenantIds = subscriptions.map((s) => s.tenantId)
  return mockTenants.filter((t) => tenantIds.includes(t.tenantId))
}

/**
 * Get active tenant count
 */
export function getActiveTenantCount(): number {
  return mockTenants.filter((t) => t.status === 'active').length
}

/**
 * Default tenant for demo purposes (Solo)
 */
export const DEFAULT_DEMO_TENANT = soloTenant

/**
 * Default subscription for demo
 */
export const DEFAULT_DEMO_SUBSCRIPTION = soloSubscription
